import { useState } from "react";

import { AdminCouponsEndpoints, AdminMarketPlaceEndpoints, AdminProductsEndpoints } from "@olagg/api-hooks";
import { Bonox, CreateProductParams, Product, ProductsCodesBatchUpload, UpdateProductParams } from "@olagg/db-types";
import { fetcher } from "@olagg/api-hooks";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { IUseProducts } from "@olagg/api-hooks/interfaces/IUseProducts";
import Cookies from "js-cookie";

export interface BonoxFilter {
  name?: string;
}

const useProduct = () => {
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [bonoxs, setBonoxs] = useState<Bonox[]>([]);
  const [product, setProduct] = useState<Product>();
  const [categories, setCategories] = useState<string[]>([]);
  const [itemsRegions, setItemsRegions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const navigate = useNavigate();

  const allBonoxs = async (filter: BonoxFilter) => {
    setIsLoading(true);
    fetcher(AdminProductsEndpoints.allBonoxs(filter))
      .then(response => {
        setBonoxs(response);
        console.log(response);
        setIsLoading(false);
      })
  }

  const allCategories = async () => {
    setIsLoading(true);
    fetcher(AdminProductsEndpoints.allCategories())
      .then(response => {
        setCategories(response.categories);
        setIsLoading(false);
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.message,
          position: 'bottom-right'
        });
        setIsLoading(false);
      })
  }

  const allItemsRegions = async () => {
    setIsLoading(true);
    fetcher(AdminMarketPlaceEndpoints.regions())
      .then(response => {
        setItemsRegions(response.regions);
        setIsLoading(false);
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.message,
          position: 'bottom-right'
        });
        setIsLoading(false);
      })
  }

  const all = async (filter: IUseProducts) => {
    setIsLoading(true);
    fetcher(AdminProductsEndpoints.all(filter))
      .then(response => {
        setCount(response.meta.count);
        setProducts(response.products);
        setIsLoading(false);
      })
  }

  const createProduct = async (data: CreateProductParams) => {
    try {
      const response = await fetcher(AdminProductsEndpoints.create(data));
      toast({
        title: 'Producto creado',
        status: 'success',
        duration: 3000,
        position: 'top-right',
        isClosable: true,
      });
      const created = response?.product || response;
      if (created?.source === 'LocalProduct' && created?.id) {
        navigate(`/products/${created.id}/inventario`);
      } else {
        navigate('/products');
      }
      return created;
    } catch (error: any) {
      toast({
        title: 'Error',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error.errors,
        position: 'bottom-right',
      });
      return null;
    }
  }

  const getProduct = async (id: string) => {
    setIsLoading(true);
    fetcher(AdminProductsEndpoints.find(id))
      .then(response => {
        setProduct(response.product);
        setIsLoading(false);
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.message,
          position: 'bottom-right'
        });
        setIsLoading(false);
      })
  }

  const editProduct = async (id: string, data: UpdateProductParams) => {
    fetcher(AdminProductsEndpoints.update(id, data))
      .then(() => {
        toast({
          title: 'Producto actualizado',
          status: 'success',
          duration: 3000,
          position: 'top-right',
          isClosable: true,
        })
        navigate('/tienda');
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.errors,
          position: 'bottom-right'
        })
      })
  }

  const generateCodes = async (productId: string, quantity: number) => {
    fetcher(AdminCouponsEndpoints.batchCreate(productId, quantity))
      .then(() => {
        toast({
          title: 'Códigos agregados exitosamente',
          description: `Se agregaron ${quantity} códigos`,
          status: 'success',
          duration: 3000,
          position: 'top-right',
        });
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.errors,
          position: 'bottom-right'
        })
      })
  }

  const exportCodes = async (productId: string) => {
    setIsLoading(true);
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL}/admin/coupons/export?productId=${productId}`;
      const token = Cookies.get('auth._token.local') || null
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'text/csv'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${new Date().toLocaleDateString('es')}_cupones_productos.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setIsLoading(false)
      toast({
        title: 'Códigos exportados exitosamente',
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.log(error)
      toast({
        title: 'Error',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error.message,
        position: 'bottom-right'
      });
    }
  };

  const removeCode = async (productId: string, codeId: string) => {
    fetcher(AdminProductsEndpoints.removeCode(productId, codeId))
      .then(() => {
        toast({
          title: 'Código eliminado exitosamente',
          status: 'success',
          duration: 3000,
          position: 'top-right',
        });
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.errors,
          position: 'bottom-right'
        })
      })
  }

  const deleteProduct = async (id: string) => {
    fetcher(AdminProductsEndpoints.delete(id))
      .then(() => {
        toast({
          title: 'Producto eliminado',
          colorScheme: 'olaggPink',
          status: 'success',
          position: 'bottom-right'
        });
        window.location.reload();
      })
      .catch((error: Error) => {
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.message,
          position: 'bottom-right'
        });
      })
  }

  return {
    products,
    product,
    bonoxs,
    isLoading,
    count,
    all,
    allBonoxs,
    allCategories,
    allItemsRegions,
    itemsRegions,
    categories,
    createProduct,
    getProduct,
    editProduct,
    deleteProduct,
    generateCodes,
    exportCodes,
    removeCode
  }
}

export default useProduct;